const FRAME_PATH = '/jsmol/index.htm';

/**
 * URL of the JSmol iframe. Shared by the <iframe src> and by resolveFrameOrigin so the
 * origin we trust is derived from exactly the URL the browser is asked to load.
 */
export const frameSrc = src2 => `${src2}${FRAME_PATH}`;

function parse(url, base) {
  try {
    return new URL(url, base);
  } catch {
    return null;
  }
}

/**
 * Works out which origin the viewer iframe is served from, in the two forms postMessage
 * needs, so that messages are only accepted from, and only sent to, that origin.
 *
 * `page` describes the host page: { baseURI, href, origin }. It is passed in rather than
 * read from `window`, so this can be exercised without a DOM and is only ever called
 * from handlers and effects. Reading `window` while rendering breaks server-side
 * rendering.
 *
 * Returns `{ origin, target }`:
 *   origin  compared against `event.origin` on inbound messages
 *   target  `targetOrigin` for outbound `postMessage`
 * or `null` when no usable origin can be derived. Callers must then neither send nor
 * accept messages: failing closed is the point of the origin check.
 *
 * Why this is not just `new URL(...).origin`:
 *  - The iframe `src` resolves against the document's base URL, which `<base href>` can
 *    move to another origin, so resolve against `baseURI` rather than the page URL.
 *  - For schemes the URL spec treats as non-special (capacitor://, ionic://, ...)
 *    `URL#origin` is the string "null". That never equals a real `event.origin`, and
 *    `postMessage(msg, "null")` throws a SyntaxError. The page still has a real origin at
 *    runtime, so when the iframe shares the page's scheme and host use `window`'s own
 *    notion of "same origin" (`origin` for inbound, the special target "/" for outbound).
 *  - file: URLs are not supported: their origin is opaque and JSmol cannot load j2s files
 *    from them anyway.
 */
export default function resolveFrameOrigin(src2, page) {
  const sameOrigin =
    page.origin && page.origin !== 'null'
      ? { origin: page.origin, target: '/' }
      : null;

  const url = parse(frameSrc(src2), page.baseURI);
  if (!url) return sameOrigin;
  if (url.protocol === 'file:') return null;
  if (url.origin !== 'null') return { origin: url.origin, target: url.origin };

  const pageUrl = parse(page.href);
  if (
    pageUrl &&
    url.protocol === pageUrl.protocol &&
    url.host === pageUrl.host
  ) {
    return sameOrigin;
  }
  return null;
}
